/**
 * Quality Score — the canonical definition.
 *
 * Every consumer of a quality score — dashboard KPIs, sparkline charts,
 * call-stats, per-agent leaderboards, quality distribution — must derive it the
 * same way at the same point (as close to the data source as possible), so the
 * same underlying record reads the same number everywhere (see WS1-T5).
 *
 * Scores live on one scale, 0-100, and are stored that way. A legacy heuristic
 * used to guess otherwise: any sub-metric <= 10 was assumed to be an old 0-10
 * rubric value and multiplied by ten. It was removed in #614 after a production
 * measurement (2026-09-06, D1 and its Postgres mirror, 29 389 scored analyses)
 * found no record of that rubric at all — the seven candidates it would have
 * "fixed" were current-model analyses from June-July 2026, six of them carrying
 * the model's own `qualityScore` above 10, which the 0-10 rubric cannot
 * produce. What the heuristic did instead was inflate 494 genuine low scores in
 * 457 analyses tenfold: a real 5 out of 100 was shown as 50.
 *
 * WS7-T12: the product used to carry *four* incompatible answers to "what is
 * this call's quality score" — the LLM's own `qualityScore` field, the mean of
 * the seven sub-metrics computed per-record, the mean of seven per-column
 * averages, and a `qualityScore || overall` fallback whose `overall` had no
 * writer at all. Bottom Performers filtered one of them against a threshold
 * derived from another. `computeCanonicalQualityScore` below is now the single
 * answer; `canonicalQualityScoreSql` (packages/database) is its SQL twin, and a
 * contract test pins the two together.
 *
 * `crmScore` (CRM card completeness) was dropped from this set later: it is
 * often not applicable to a call at all, and it measures data hygiene rather
 * than conversation quality, so it no longer belongs in an unweighted quality
 * mean. It is still extracted and stored on the analysis (`extractAnalysisMetrics`)
 * as a separate compliance indicator — this only removes it from the average.
 */

import type { QualityWeights } from '@aica/api-types';

/**
 * The six scored dialogue components, in the order they are presented.
 *
 * SSOT for "which sub-metrics make up the quality score" — previously spelled
 * out separately in `aggregation.ts` (via METRIC_NAMES), `analytics.ts`, and
 * implicitly in every SQL aggregate.
 */
export const QUALITY_SUB_METRIC_KEYS = [
  'greetingScore',
  'needsScore',
  'clarityScore',
  'objectionScore',
  'closingScore',
  'politenessScore',
] as const;

export type QualitySubMetricKey = (typeof QUALITY_SUB_METRIC_KEYS)[number];

/**
 * The largest |sub-metric| a call may carry and still be scored by weights (#874).
 *
 * A valid sub-metric sits on 0-100 (`records.ts`: int 0..100), but the extraction
 * path does not bound what the model writes — `1e18` reaches storage. The weighted
 * formula is exact integer arithmetic (`u = floor(10·v + 0.5)`), and the SQL twins
 * narrow to 64-bit integers for it: Postgres raises on overflow (failing the whole
 * aggregate), SQLite saturates and disagrees with JS. So a call with a present
 * sub-metric beyond this bound is not read through weights at all — it gets
 * today's unweighted mean, in JS, D1 and Postgres alike.
 *
 * 1e6 is 10 000× the real scale (a model slip, not a rubric value, lands far
 * beyond it) and still tiny for the arithmetic: u ≤ 1e7+1, so Σ w·u ≤ 6·10·1e7 =
 * 6e8 — inside even 32-bit integers, hence inside every engine's narrowing.
 * Inclusive: `|v| = 1e6` is weighted, `1e6 + 0.5` is not.
 */
export const QUALITY_WEIGHTED_SUB_METRIC_BOUND = 1_000_000;

/**
 * The largest |sub-metric| that counts as a score at all (#1889). Beyond it the
 * value reads as absent — the way `Infinity` (`JSON.parse` of `1e400`) always
 * did in JS — in JS, D1 and Postgres alike.
 *
 * Without it the engines part ways at the edge of `double`: SQLite reads `1e400`
 * as `Infinity` and averages it in, Postgres refuses to cast it (and `1e-400`,
 * which underflows) to `double precision` and fails the whole aggregate, and two
 * finite `1e308` overflow the sum — `Infinity` in JS and SQLite, an error in
 * Postgres. Six values of at most 1e300 sum far inside `double`, so no engine
 * can overflow on the mean. No rubric value comes anywhere near it: this changes
 * nothing for a number a model could mean as a score.
 */
export const QUALITY_SUB_METRIC_LIMIT = 1e300;

/**
 * Round a stored quality score for display: one decimal, nothing else.
 *
 * This used to also apply the legacy 0-10 heuristic, which made it unsafe to
 * call twice — a raw 1 became 10 became 100. It is now idempotent, because a
 * stored score is already on the scale it is shown on.
 */
export function normalizeQualityScore(value: number): number;
export function normalizeQualityScore(value: null): null;
export function normalizeQualityScore(value: number | null): number | null;
export function normalizeQualityScore(value: number | null): number | null {
  if (value === null) return null;
  return Math.round(value * 10) / 10;
}

/**
 * Does the top level of a JSON object text name one key twice? (`{"a":1,"a":2}`)
 *
 * `JSON.parse` and Postgres JSONB keep the LAST of two equal keys, SQLite's
 * `json_extract` the FIRST — the stored text is the one place the engines can read
 * different weights from the same bytes, so a repeat is an unusable value
 * everywhere (`parseStoredQualityWeights`). Only the top level is scanned: valid
 * weights are a flat object of numbers, any nesting is already invalid, and the
 * D1 twin's `json_each` sees the top level only. Keys are compared decoded
 * (`"a"` is `"a"`). Expects valid JSON (the caller has parsed it already).
 */
function hasDuplicateTopLevelKeys(text: string): boolean {
  const start = text.search(/\S/);
  if (start < 0 || text[start] !== '{') return false;

  const seen = new Set<string>();
  let depth = 0;
  let expectKey = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      let end = i + 1;
      while (text[end] !== '"') end += text[end] === '\\' ? 2 : 1;
      if (depth === 1 && expectKey) {
        const key = JSON.parse(text.slice(i, end + 1)) as string;
        if (seen.has(key)) return true;
        seen.add(key);
        expectKey = false;
      }
      i = end;
    } else if (ch === '{' || ch === '[') {
      depth += 1;
      if (depth === 1) expectKey = true;
    } else if (ch === '}' || ch === ']') {
      depth -= 1;
    } else if (ch === ',' && depth === 1) {
      expectKey = true;
    }
  }
  return false;
}

/**
 * The ONE reader of a stored `quality_weights` TEXT (the D1 column): the parsed
 * value, or `null` when the text is unusable as stored weights — `null`/SQL NULL,
 * not JSON, or a top-level key written twice (`hasDuplicateTopLevelKeys`).
 *
 * Feed the result to `canonicalizeQualityWeights`, and put it — never the raw
 * text — into any per-project map the Postgres twin reads (`CAST(map AS jsonb)`
 * would silently keep the last of two equal keys, SQLite's `json_extract` the
 * first). A JSON `null` text parses to `null` as well: the same "equal weights".
 */
export function parseStoredQualityWeights(stored: string | null | undefined): unknown {
  if (stored === null || stored === undefined) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return null;
  }
  return hasDuplicateTopLevelKeys(stored) ? null : parsed;
}

/**
 * The weights a project's stored `quality_weights` value stands for, or `null`
 * when the score is to be read with equal weights (#874, spec §4).
 *
 * The ONE JS implementation of the reading rule — the SQL twins express the same
 * rule over the raw column. A stored value is trusted only when all four hold,
 * otherwise it reads as `null` (equal weights), never as an error:
 *  1. it is a JSON object (not an array, `null` or a primitive);
 *  2. it carries at least one current key (extra keys are ignored — stored JSON
 *     may be newer than this code);
 *  3. every present current key is a whole number 0..10 (`3.0` is `3`; a string,
 *     boolean, JSON null, fraction, negative or `> 10` spoils the WHOLE value —
 *     silently skipping one key would shift the shares of the others);
 *  4. the present weights sum to more than zero.
 *
 * Then a missing current key takes the mean of the present weights rounded
 * half-up to a whole number (`floor((2Σ + n) / (2n))`), and weights that all
 * come out equal read as `null` too — so "equal" has exactly one representation
 * and goes through today's expression byte for byte (D3).
 */
export function canonicalizeQualityWeights(stored: unknown): QualityWeights | null {
  if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) return null;
  const record = stored as Record<string, unknown>;

  let sum = 0;
  let present = 0;
  for (const key of QUALITY_SUB_METRIC_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue;
    const value = record[key];
    if (
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < 0 ||
      value > 10
    ) {
      return null;
    }
    sum += value;
    present += 1;
  }
  if (present === 0 || sum === 0) return null;

  const fallback = Math.floor((2 * sum + present) / (2 * present));
  const weights = {} as Record<QualitySubMetricKey, number>;
  for (const key of QUALITY_SUB_METRIC_KEYS) {
    weights[key] = Object.prototype.hasOwnProperty.call(record, key)
      ? (record[key] as number)
      : fallback;
  }

  const first = weights[QUALITY_SUB_METRIC_KEYS[0]];
  if (QUALITY_SUB_METRIC_KEYS.every((key) => weights[key] === first)) return null;
  return weights as QualityWeights;
}

/**
 * The canonical quality score of a single analysis: the mean of whichever of
 * the six sub-metrics the analysis actually carries, each first placed on the
 * 0-100 scale — unweighted by default, weighted by the project's weights when
 * they are given.
 *
 * Why the sub-metrics and not the LLM's own `qualityScore` field: this number
 * is reproducible from data we store, and it is *explainable* — the score-
 * breakdown chart the user already sees is literally its summands. The model's
 * aggregate opinion could disagree with its own component scores, and did.
 *
 * Absent sub-metrics are skipped, never read as 0 — a call the rubric never
 * scored ("other" category analyses store `metrics = {}` by design) yields
 * `null`, which every consumer must render as "no score", never as a zero
 * dragging an average down. A number beyond `QUALITY_SUB_METRIC_LIMIT` is
 * skipped the same way.
 *
 * A genuinely bad component is kept as it stands: 7 means 7, so a "< 60" rule
 * fires for the very worst calls instead of reading them as a tenfold-smaller
 * legacy value. That guarantee used to be opt-in per caller
 * (`assumeHundredScale`), which meant the routing step and the pages a customer
 * reads answered the same question differently; it now holds everywhere.
 *
 * `weights` — the project's weights (`undefined` / `null` = equal). The value is
 * re-read through `canonicalizeQualityWeights`, so it is safe to pass what
 * `parseStoredQualityWeights` returned for the stored text (NOT `JSON.parse` of
 * it: a text naming a key twice must read as "none", and only the former does),
 * or a `QualityWeights` already read — an object that is not usable weights
 * reads as equal weights, never as an error. Decisions per call, over the
 * sub-metrics `P` the call carries:
 *  1. `P` empty → `null`;
 *  2. every weight of `P` is 0 → `null` (D4: "no score", not a zero);
 *  3. the weights of `P` are all equal (the call may lack a sub-metric, so
 *     `2,2,2,2,2,5` on a chat without clarity is equal) → today's formula, the
 *     mean of the raw values rounded with `Math.round(x·10)/10`;
 *  4. otherwise the weighted formula on integers, identical in JS, SQLite and
 *     Postgres: `u = floor(v·10 + 0.5)` per sub-metric, `S = Σ w·u`, `W = Σ w`,
 *     `T = floor((2S + W) / (2W))`, score `T / 10`. The built-in `round`s of the
 *     three engines disagree on ties (`round(0.15, 1)`); `floor` on integers
 *     does not.
 *
 * A call with a present sub-metric beyond `QUALITY_WEIGHTED_SUB_METRIC_BOUND` is
 * not weighted at all — rule 4 AND rule 2 are skipped, and it reads rule 3's
 * unweighted mean (the integer arithmetic of rule 4 must not overflow in SQL).
 */
export function computeCanonicalQualityScore(
  metrics: Record<string, unknown> | null | undefined,
  weights?: QualityWeights | null
): number | null {
  return computeCanonicalQualityScoreWith(
    metrics,
    weights ? canonicalizeQualityWeights(weights) : null
  );
}

/**
 * Does the call carry this sub-metric as a score? A number within
 * `QUALITY_SUB_METRIC_LIMIT` — `NaN` and `±Infinity` fail the comparison too.
 * The one rule for every reader of a sub-metric (the score, its breakdown, the
 * per-metric averages) and for the writer, so none of them counts a value the
 * others skip; the SQL twins spell it in `quality-score-sql.ts`.
 */
export function isCountedSubMetric(value: unknown): value is number {
  return typeof value === 'number' && Math.abs(value) <= QUALITY_SUB_METRIC_LIMIT;
}

/**
 * `computeCanonicalQualityScore` for weights that have already been through
 * `canonicalizeQualityWeights` (`null` = equal). Loops over many analyses read
 * each project's weights once (`createCanonicalWeightsMemo`) and score every
 * analysis with this, instead of canonicalizing the same object per row.
 *
 * Allocates nothing: the unweighted mean — every call of every project today — is
 * one pass over the six keys, the weighted formula a second one.
 */
export function computeCanonicalQualityScoreWith(
  metrics: Record<string, unknown> | null | undefined,
  effective: QualityWeights | null
): number | null {
  if (!metrics || typeof metrics !== 'object') return null;

  let count = 0;
  let sum = 0;
  let withinBound = true;
  for (const key of QUALITY_SUB_METRIC_KEYS) {
    const value = metrics[key];
    if (!isCountedSubMetric(value)) continue;
    count += 1;
    sum += value;
    if (Math.abs(value) > QUALITY_WEIGHTED_SUB_METRIC_BOUND) withinBound = false;
  }
  if (count === 0) return null;

  if (effective && withinBound) {
    let totalWeight = 0;
    let weightedSum = 0;
    let firstWeight = -1;
    let unequal = false;
    for (const key of QUALITY_SUB_METRIC_KEYS) {
      const value = metrics[key];
      if (!isCountedSubMetric(value)) continue;
      const weight = effective[key];
      if (firstWeight < 0) firstWeight = weight;
      else if (weight !== firstWeight) unequal = true;
      totalWeight += weight;
      weightedSum += weight * Math.floor(value * 10 + 0.5);
    }
    if (totalWeight === 0) return null;
    if (unequal) {
      return Math.floor((2 * weightedSum + totalWeight) / (2 * totalWeight)) / 10;
    }
  }

  return Math.round((sum / count) * 10) / 10;
}

/**
 * The share of each criterion in THIS call's score, in percent (#874, spec §8 —
 * the card's "weight 25 %" next to each criterion row), or `null` when the call
 * is scored by the plain mean and no share is due: equal (or absent, or
 * unusable) weights, weights equal over the sub-metrics the call carries, no
 * sub-metric at all, every carried one weighing 0, or a sub-metric beyond
 * `QUALITY_WEIGHTED_SUB_METRIC_BOUND`. Exactly the branches of
 * `computeCanonicalQualityScoreWith`, so a share is shown only when the score
 * was actually weighted. Shares are over the carried sub-metrics only — a
 * criterion the call was not rated on gets none, and the others sum to 100.
 */
export function qualityWeightShares(
  metrics: Record<string, unknown> | null | undefined,
  weights: unknown
): Partial<Record<QualitySubMetricKey, number>> | null {
  return qualityWeightSharesWith(metrics, canonicalizeQualityWeights(weights));
}

/**
 * `qualityWeightShares` over weights already through the reading rule — the
 * pair of `computeCanonicalQualityScoreWith`, for a caller that canonicalized
 * once and scores with the same object.
 */
export function qualityWeightSharesWith(
  metrics: Record<string, unknown> | null | undefined,
  effective: QualityWeights | null
): Partial<Record<QualitySubMetricKey, number>> | null {
  if (!effective || !metrics || typeof metrics !== 'object') return null;
  const carried = QUALITY_SUB_METRIC_KEYS.filter((key) =>
    isCountedSubMetric(metrics[key])
  );
  if (carried.length === 0) return null;
  if (
    carried.some(
      (key) => Math.abs(metrics[key] as number) > QUALITY_WEIGHTED_SUB_METRIC_BOUND
    )
  ) {
    return null;
  }
  const total = carried.reduce((sum, key) => sum + effective[key], 0);
  const first = effective[carried[0]!];
  if (total === 0 || carried.every((key) => effective[key] === first)) return null;
  const shares: Partial<Record<QualitySubMetricKey, number>> = {};
  for (const key of carried) shares[key] = (effective[key] / total) * 100;
  return shares;
}

/**
 * Did the weights of the time of analysis give this call a different score from
 * the current ones (#874, spec §8)? The card's line "the CRM and alert score of
 * this call was scored with the weights of the time of analysis" — next to the
 * scorer, so no surface implements the rule twice.
 *
 * `atAnalysis` is the field as the analysis response carries it: an object,
 * `null` (equal weights, recorded as such) or `undefined` (not recorded — no
 * line, there is nothing to compare). `current` — the project's weights now,
 * read through `canonicalizeQualityWeights` here (`null` and any equal sheet
 * are one "equal"). `cardScore` — the score the card already computed with
 * them, when the caller has it.
 *
 * The scores are compared, not the weights: weights that differ can score a
 * call alike (a weight on a criterion the call lacks, weights in the same
 * proportion, sub-metrics that are all equal), and the line would then name a
 * difference nobody can find. The fallback of §8: when the two agree, the
 * number that actually went out — the stored `metrics.qualityScore` — is
 * compared with the card's too.
 */
export function scoredWithEarlierWeights(input: {
  metrics: Record<string, unknown> | null | undefined;
  current: unknown;
  atAnalysis: unknown;
  cardScore?: number | null;
}): boolean {
  if (input.atAnalysis === undefined) return false;
  const onCard =
    input.cardScore !== undefined
      ? input.cardScore
      : computeCanonicalQualityScoreWith(
          input.metrics,
          canonicalizeQualityWeights(input.current)
        );
  if (
    computeCanonicalQualityScoreWith(
      input.metrics,
      canonicalizeQualityWeights(input.atAnalysis)
    ) !== onCard
  ) {
    return true;
  }
  const stored = input.metrics?.qualityScore;
  return typeof stored === 'number' && Number.isFinite(stored) && stored !== onCard;
}

/**
 * `canonicalizeQualityWeights` remembered by object identity, for ONE loop: rows
 * of a project share one weights object, so the reading rule runs once per project
 * and not once per analysis. A fresh memo per loop — the cache never outlives the
 * values it was filled from, so a weights object edited between two loops is not
 * served stale.
 */
export function createCanonicalWeightsMemo(): (stored: unknown) => QualityWeights | null {
  const seen = new WeakMap<object, QualityWeights | null>();
  return (stored) => {
    if (stored === null || typeof stored !== 'object') return null;
    const hit = seen.get(stored);
    if (hit !== undefined) return hit;
    const canonical = canonicalizeQualityWeights(stored);
    seen.set(stored, canonical);
    return canonical;
  };
}

/**
 * The stored `analyses.metrics` JSON as an object — `{}` for absent, broken or
 * non-object JSON. One reading for every place that scores a stored analysis
 * (the records list, the search index's quality band — #1358), so they cannot
 * disagree about what a malformed row means. `'null'` and `'[]'` parse fine
 * but are not metrics; reading a field off them would fail the caller.
 */
export function parseAnalysisMetricsJson(
  metricsJson: string | null | undefined
): Record<string, unknown> {
  if (!metricsJson) return {};
  try {
    const parsed: unknown = JSON.parse(metricsJson);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

export type QualitySentiment = 'positive' | 'neutral' | 'negative';

/**
 * The records list's "sentiment" is a band of the canonical quality score, and
 * two things must agree on it: the label on a row and the list's sentiment
 * filter (#1203). They used to hold one copy of the thresholds each.
 */
const QUALITY_SENTIMENT_POSITIVE_MIN = 80;
const QUALITY_SENTIMENT_NEUTRAL_MIN = 50;

export function qualitySentiment(score: number | null): QualitySentiment | null {
  if (score == null) return null;
  if (score >= QUALITY_SENTIMENT_POSITIVE_MIN) return 'positive';
  if (score >= QUALITY_SENTIMENT_NEUTRAL_MIN) return 'neutral';
  return 'negative';
}

/**
 * The integer `qaScoreMin`/`qaScoreMax` pair that selects one sentiment band.
 * The list filter reads `max = M` as `score < M + 1` and leaves the ends of the
 * scale open, so these bounds select exactly the scores `qualitySentiment`
 * labels with the band — 79.5 is neutral in both places.
 */
export function qualitySentimentScoreRange(sentiment: QualitySentiment): {
  min: number;
  max: number;
} {
  switch (sentiment) {
    case 'positive':
      return { min: QUALITY_SENTIMENT_POSITIVE_MIN, max: 100 };
    case 'neutral':
      return {
        min: QUALITY_SENTIMENT_NEUTRAL_MIN,
        max: QUALITY_SENTIMENT_POSITIVE_MIN - 1,
      };
    case 'negative':
      return { min: 0, max: QUALITY_SENTIMENT_NEUTRAL_MIN - 1 };
  }
}

/**
 * Score bands for classifying a single 0-100 metric as a strength or a focus
 * area (WS7-T22). Previously the agent dashboard decided this three different
 * ways for adjacent concepts — a metric was a "strength" at >= 80, a "focus
 * area" chip at < 60, yet the single headline focus area surfaced at < 70 — so
 * the same 65-scoring metric was simultaneously "not a focus area" (no chip)
 * and "your focus area" (headline). These two thresholds are now the one place
 * that decides it, matching the 80/60 band the UI already colors scores by
 * (`score-color.ts scoreBand`).
 */
export const QUALITY_STRENGTH_THRESHOLD = 80;
export const QUALITY_FOCUS_THRESHOLD = 60;

/**
 * Цель по метрике, объявленной зоной роста.
 *
 * Правило жило двумя разными литералами на ОДНОМ экране агента: карточка
 * «фокус» показывала фиксированные 70, недельный план — «текущий + 15», и для
 * одной и той же метрики агент читал две несовпадающие цели. Шаг «+15» вдобавок
 * терял смысл у нижнего края (0 → 15, всё ещё провал), поэтому цель не
 * опускается ниже выхода из красного.
 */
export function qualityTargetScore(score: number): number {
  return Math.min(100, Math.max(score + 15, QUALITY_FOCUS_THRESHOLD));
}

/** A metric at or above the strength threshold (>= 80). */
export function isStrengthScore(score: number): boolean {
  return score >= QUALITY_STRENGTH_THRESHOLD;
}

/**
 * Метрика ниже порога фокуса (< 60) — зона роста. Ноль в том числе.
 *
 * Раньше здесь стоял guard `score > 0` с обоснованием «ноль значит „не
 * оценивали", а не „плохо"». В хранимых данных это неверно: ноль ставит сама
 * рубрика за проваленный критерий (`no_close` в closing — 1277 записей за 30
 * дней в проде), а «не оценивали» выражается ОТСУТСТВИЕМ метрики. Значение 0
 * приходило сюда лишь потому, что агрегат подставлял его за отсутствие
 * (`avgMetrics[id] || 0`), и guard прикрывал ту подстановку — заодно пряча из
 * зон роста ровно те метрики, которые провалены сильнее всего.
 *
 * Подстановка убрана (`buildQualityMetrics` отдаёт `null`), поэтому guard
 * снят: сюда попадает только настоящий счёт. Вызывающий обязан сам не звать
 * эту функцию для неоценённой метрики — `null` не является счётом.
 */
export function isFocusAreaScore(score: number): boolean {
  return score < QUALITY_FOCUS_THRESHOLD;
}
